//  src/components/pages/backtest/hooks/usePortfolioActions.js

import { useState, useCallback, useEffect } from "react";
import { toast } from 'react-toastify';

// ⚠️ API_URL은 프로젝트 환경 변수에서 가져오도록 설정 필요
const API_URL = process.env.REACT_APP_API_BASE_URL; 

// =========================================================
// 💡 usePortfolioActions Hook 정의
// =========================================================
export function usePortfolioActions(auth, setup, t, navigate) {
    // 1. 상태 정의
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [tempHoldings, setTempHoldings] = useState([]);
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false); 
    const [isBenchmarkLoadModalOpen, setIsBenchmarkLoadModalOpen] = useState(false); 
    const [userPortfolios, setUserPortfolios] = useState([]); 
    const [isPortfoliosLoading, setIsPortfoliosLoading] = useState(false); 

    // 2. 상태 변경 시 모달 닫기
    useEffect(() => {
        // 입력 상태(rows, benchmark 등)가 변경되면 로드 모달을 닫습니다.
        setIsLoadModalOpen(false);
        setIsBenchmarkLoadModalOpen(false);
    }, [setup.rows, setup.benchmark, setup.benchmarkRows]);

    // 3. 포트폴리오 목록 불러오기 (메인/벤치마크 공통)
    const fetchUserPortfolios = useCallback(async (forBenchmark = false) => {
        if (!auth.isAuthenticated) {
            toast.error(t('backtest.loadError.loginRequired') || "로그인이 필요합니다.");
            return;
        }
        if (isPortfoliosLoading) return;

        // 모달 닫기 토글 로직
        const isCurrentModalOpen = forBenchmark ? isBenchmarkLoadModalOpen : isLoadModalOpen;
        if (isCurrentModalOpen) {
            (forBenchmark ? setIsBenchmarkLoadModalOpen : setIsLoadModalOpen)(false);
            return;
        }

        setIsPortfoliosLoading(true);
        
        try {
            // ⚠️ API_URL 확인 필요
            const res = await fetch(`${API_URL}/portfolios/my`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${auth.user.id_token}` },
            });

            if (!res.ok) throw new Error('Failed to fetch user portfolios');

            const data = await res.json();
            setUserPortfolios(data);
            
            if (data.length > 0) {
                (forBenchmark ? setIsBenchmarkLoadModalOpen : setIsLoadModalOpen)(true);
            }
            else toast.info(t('backtest.noPortfolios') || "저장된 포트폴리오가 없습니다.");
        } catch (error) {
            toast.error(t('backtest.loadError.fetchFailed') || "포트폴리오 목록을 불러오는 데 실패했습니다.");
            setUserPortfolios([]);
        } finally {
            setIsPortfoliosLoading(false);
        }
    }, [auth, isPortfoliosLoading, isLoadModalOpen, isBenchmarkLoadModalOpen, t]);

    // 4. 주 포트폴리오 로드 핸들러
    const handleLoadPortfolio = useCallback((portfolio) => {
        const holdings = portfolio.initialHoldings; 
        const holdingTotalWeight = holdings.reduce((sum, h) => sum + Number(h.weight), 0) * 100;
        
        if (!holdings || holdings.length === 0 || holdingTotalWeight < 0.1) {
            toast.error(t('backtest.loadError.emptyHoldings') || "선택된 포트폴리오에 유효한 종목이 없습니다.");
            return;
        }
        
        const newRows = holdings
            .filter(h => h.ticker && Number(h.weight) > 0)
            .map(h => ({
                ticker: h.ticker,
                weight: (Number(h.weight) * 100).toFixed(2).toString() 
            }));

        setup.setRows(newRows);
        setIsLoadModalOpen(false); 
        
        if (Math.abs(holdingTotalWeight - 100) > 0.1) {
            toast.warn(t('backtest.weightSumWarning', { sum: holdingTotalWeight.toFixed(2) }) || `불러온 포트폴리오의 비중 합이 ${holdingTotalWeight.toFixed(2)}% 입니다. 백테스트 실행 전에 100%로 조정해 주세요.`);
        }

        setup.setMessage(t('backtest.portfolioLoaded', { name: portfolio.name }) || `${portfolio.name} 포트폴리오를 불러왔습니다. 이제 백테스트를 실행하세요.`);
        toast.success(t('backtest.portfolioLoadedSuccess') || "포트폴리오 로드 완료!");
        
        // 로드 후 URL 클린업
        navigate(window.location.pathname, { replace: true });
    }, [setup, t, navigate]);
    
    // 5. 벤치마크 포트폴리오 로드 핸들러
    const handleLoadBenchmarkPortfolio = useCallback((portfolio) => {
        const holdings = portfolio.initialHoldings; 
        const holdingTotalWeight = holdings.reduce((sum, h) => sum + Number(h.weight), 0) * 100;
        
        if (!holdings || holdings.length === 0 || holdingTotalWeight < 0.1) {
            toast.error(t('backtest.loadError.emptyHoldings') || "선택된 포트폴리오에 유효한 종목이 없습니다.");
            return;
        }
        
        const newBenchmarkRows = holdings
            .filter(h => h.ticker && Number(h.weight) > 0)
            .map(h => ({
                ticker: h.ticker,
                weight: (Number(h.weight) * 100).toFixed(2).toString() 
            }));

        setup.setBenchmarkRows(newBenchmarkRows);
        setup.setBenchmarkInputMode('portfolio');
        setIsBenchmarkLoadModalOpen(false); 
        
        if (Math.abs(holdingTotalWeight - 100) > 0.1) {
            toast.warn(t('backtest.weightSumWarning', { sum: holdingTotalWeight.toFixed(2) }) || `불러온 포트폴리오의 비중 합이 ${holdingTotalWeight.toFixed(2)}% 입니다. 백테스트 실행 전에 100%로 조정해 주세요.`);
        }

        toast.success(t('backtest.portfolioLoadedSuccess', { name: portfolio.name }) || `${portfolio.name} 포트폴리오를 벤치마크로 로드 완료!`);
    }, [setup, t]);

    // 6. 성과 추적 시작 (API 호출)
    const handleStartTrackingPortfolio = useCallback(async (portfolio) => {
        if (!auth.isAuthenticated) {
            toast.error(t('backtest.loadError.loginRequired'));
            auth.signinRedirect();
            return;
        }
        
        setIsPortfoliosLoading(true);

        try {
            // ... (기존 BacktestPage.js의 handleStartTrackingPortfolio 로직)
            const idToken = auth.user.id_token;
            const originalHoldings = portfolio.initialHoldings; 

            // 1. 기존 포트폴리오 삭제
            const deleteRes = await fetch(`${API_URL}/portfolios/${portfolio.portfolioId}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${idToken}` },
            });

            if (!deleteRes.ok && deleteRes.status !== 404) {
                throw new Error(t('backtest.trackingResetError.deleteFailed') || `기존 포트폴리오 삭제 실패: ${deleteRes.status}`);
            }

            // 2. 새 포트폴리오 생성
            const createRes = await fetch(`${API_URL}/portfolios`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ name: portfolio.name, holdings: originalHoldings, isPrivate: false }),
            });

            if (!createRes.ok) {
                 const errorBody = await createRes.json();
                 throw new Error(errorBody.error || t('backtest.trackingResetError.createFailed') || `새 포트폴리오 생성 실패: ${createRes.status}`);
            }

            toast.success(t('backtest.trackingResetSuccess', { name: portfolio.name }) || `${portfolio.name} 포트폴리오의 성과 추적이 현재 시점부터 다시 시작되었습니다.`);
            
            setIsLoadModalOpen(false); 
            await fetchUserPortfolios(false); // 목록 새로고침

        } catch (error) {
            const errorMessage = error.message.includes('up to') 
                ? t('backtest.trackingResetError.limitExceeded') || `포트폴리오 생성 제한을 초과했습니다`
                : error.message;
            toast.error(errorMessage);
        } finally {
            setIsPortfoliosLoading(false);
        }
    }, [auth, fetchUserPortfolios, t]);

    // 7. 저장 모달 열기
    const handleOpenSaveModal = useCallback(() => {
        if (!auth.isAuthenticated) {
            toast.error(t('backtest.saveError.loginRequired') || "로그인이 필요합니다.");
            return;
        }

        if (setup.validRows.length === 0) {
            toast.error(t("backtest.saveError.enterOneTickerAndWeight") || "최소 하나 이상의 종목과 비중을 입력해주세요.");
            return;
        }

        if (Math.abs(setup.totalWeight - 100) > 0.1) {
            toast.error(t("backtest.weightSumMustBe100", { totalWeight: setup.totalWeight.toFixed(2) }));
            return;
        }
        
        // 모달에 전달할 형식으로 변환 (비중: 100 -> 1.00)
        const holdingsForModal = setup.validRows.map(h => ({
            ticker: h.ticker,
            weight: Number(h.weight) / 100
        }));

        setTempHoldings(holdingsForModal);
        setShowSaveModal(true); 
    }, [auth, setup, t]);
    
    // 8. 포트폴리오 저장 완료 핸들러
    const handlePortfolioSaved = useCallback(() => {
        setShowSaveModal(false);
        setTempHoldings([]);
        toast.success(t('backtest.saveSuccess') || "포트폴리오가 성공적으로 저장되었습니다."); 
    }, [t]);


    return {
        showSaveModal, setShowSaveModal, tempHoldings,
        isLoadModalOpen, setIsLoadModalOpen, isBenchmarkLoadModalOpen, setIsBenchmarkLoadModalOpen,
        userPortfolios, isPortfoliosLoading, setIsPortfoliosLoading,
        fetchUserPortfolios,
        handleLoadPortfolio,
        handleLoadBenchmarkPortfolio,
        handleStartTrackingPortfolio,
        handleOpenSaveModal,
        handlePortfolioSaved
    };
}